import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  IOperationGroupGlobal,
  IOperationGroupPatchRequest,
  IOperationGroupPostRequest,
  IOperationGroupsSlice,
  IOperationWithTypesAndParams,
} from 'types/operations.interface'
import { toast } from 'react-toastify'
import { baseUrl } from '../../utils/axios'
import { handleErrors, formatParams, getParams } from 'utils/helpers'
import { INameId } from 'constants/globalTypes'
import { AxiosResponse } from 'axios'
import { IPaginatedResponse } from 'types/api/general.interface'

const initialState: IOperationGroupsSlice = {
  isLoading: false,
  operationGroups: [],
  totalCount: 0,
  totalPages: 0,
  singleOperationGroup: null,
}

export const getOperationGroups = createAsyncThunk(
  'operations/getOperationGroups',
  async (params: any, thunkAPI) => {
    params = formatParams(params)
    try {
      const resp: AxiosResponse<INameId> = await baseUrl.get('/operation/group', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleOperationGroup = createAsyncThunk(
  'singleOperation/getSingleOperationGroup',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<IOperationGroupGlobal<IOperationWithTypesAndParams>> =
        await baseUrl.get(`operation/group/${id}`)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addOperationGroup = createAsyncThunk(
  'postOperation/addOperationGroup',
  async (operation: IOperationGroupPostRequest, thunkAPI) => {
    try {
      const resp = await baseUrl.post('operation/group', operation, thunkAPI)
      await thunkAPI.dispatch(getOperationGroups(getParams({ location })))
      return { name: resp.data.name, id: resp.data.id }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteOperationGroup = createAsyncThunk(
  'deleteOperationGroup/deleteOperationGroup',
  async (id: string | number, thunkAPI) => {
    try {
      await baseUrl.delete('operation/group/' + id, thunkAPI)
      await thunkAPI.dispatch(getOperationGroups(getParams({ location })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateOperationGroup = createAsyncThunk(
  'postOperation/updateOperationGroup',
  async (operation: IOperationGroupPatchRequest, thunkAPI) => {
    try {
      const { id } = operation
      delete operation.id
      await baseUrl.put('operation/group/' + id, operation, thunkAPI)
      return operation
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const operationGroupsSlice = createSlice({
  name: 'operationGroups',
  initialState,
  reducers: {
    clearOperationGroup: (state) => {
      state.singleOperationGroup = null
    },
    clearOperationGroupsList: (state) => {
      state.operationGroups = null
      state.totalCount = 0
      state.totalPages = 0
    },
  },
  extraReducers: {
    [getOperationGroups.pending.type]: (state) => {
      state.isLoading = true
    },
    [getOperationGroups.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<INameId> },
    ) => {
      state.isLoading = false
      state.operationGroups = payload.data
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
    },
    [getOperationGroups.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleOperationGroup.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleOperationGroup.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.singleOperationGroup = payload
    },
    [getSingleOperationGroup.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addOperationGroup.pending.type]: (state) => {
      state.isLoading = true
    },
    [addOperationGroup.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Operation added')
    },
    [addOperationGroup.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [updateOperationGroup.pending.type]: (state) => {
      state.isLoading = true
    },
    [updateOperationGroup.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Operation updated')
    },
    [updateOperationGroup.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteOperationGroup.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteOperationGroup.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Operation deleted')
    },
    [deleteOperationGroup.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearOperationGroup, clearOperationGroupsList } = operationGroupsSlice.actions
export default operationGroupsSlice.reducer
